import { useState } from "react";
import { Bell, X, Share } from "lucide-react";
import {
  isPushSupported,
  isIOSSafari,
  isStandalone,
  getNotificationState,
  enablePush,
} from "@/lib/push";

const DISMISS_KEY = "buzzn:notif-nudge-dismissed";

export function NotificationNudge() {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [permission, setPermission] = useState(getNotificationState);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  // Don't render until we know push could ever work here.
  const supported = isPushSupported();
  const iosNeedsInstall = isIOSSafari() && !isStandalone();

  // iOS in Safari can't request permission at all until installed, but it also
  // doesn't expose PushManager — so we special-case it before the support gate.
  if (dismissed) return null;
  if (!iosNeedsInstall && !supported) return null;
  if (!iosNeedsInstall && (permission === "granted" || permission === "denied")) {
    return null;
  }

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  const onEnable = async () => {
    setBusy(true);
    setError(false);
    try {
      const result = await enablePush();
      setPermission(result);
      if (result === "granted") {
        dismiss();
      }
    } catch {
      // Permission granted but the subscription couldn't be saved — keep the
      // nudge up so the user can retry instead of silently failing.
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="notification-nudge"
      className="w-full max-w-sm px-4 py-3 rounded-2xl glass-card border border-primary/30 bg-primary/10 text-sm animate-in fade-in slide-in-from-bottom-2 duration-300"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 shrink-0 text-primary">
          <Bell className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          {iosNeedsInstall ? (
            <>
              <p className="font-medium text-white">Get Buzzn alerts on your iPhone</p>
              <p className="mt-1 text-zinc-300">
                Tap <Share className="inline w-3.5 h-3.5 -mt-0.5" /> Share, then{" "}
                <span className="text-white">Add to Home Screen</span>. Open Buzzn from
                there to turn on notifications.
              </p>
            </>
          ) : (
            <>
              <p className="font-medium text-white">Turn on notifications</p>
              <p className="mt-1 text-zinc-300">
                Get alerts for friend requests, busy venues and more.
              </p>
              <button
                data-testid="button-enable-notifications"
                onClick={onEnable}
                disabled={busy}
                className="mt-2 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-semibold disabled:opacity-60"
              >
                {busy ? "Enabling…" : error ? "Try again" : "Enable notifications"}
              </button>
              {error && (
                <p data-testid="text-nudge-error" className="mt-1 text-xs text-red-400">
                  Couldn't turn on notifications. Please try again.
                </p>
              )}
            </>
          )}
        </div>
        <button
          data-testid="button-dismiss-nudge"
          onClick={dismiss}
          aria-label="Dismiss"
          className="shrink-0 text-zinc-400 hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
